import { useTranslate } from 'core/hooks/useTranslate';
import { authenticateClassroom, getClassrooms, getClassroomStudents } from 'core/utils/googleClassroom';
import { removeOBjectsFromArrayBasedOnAnotherArray } from 'core/utils/utils';
import { useEffect, useState } from 'react';

/**
 * Extrae los campos relevantes del error que devuelve la Google Classroom API.
 * El objeto de error de gapi tiene forma { result: { error: { code, message, status, details|errors: [...] } } }.
 */
export function parseClassroomError(error) {
  const details = error?.result?.error ?? error?.error ?? error ?? {};
  const reason = details?.details?.[0]?.reason ?? details?.errors?.[0]?.reason;
  return {
    code: details?.code,
    status: details?.status,
    message: details?.message,
    reason,
  };
}

/** Loguea el error de la Google Classroom API con datos accionables (no un objeto opaco). */
function logClassroomError(context, error) {
  const { code, status, message, reason } = parseClassroomError(error);
  console.error(`[GoogleClassroom] ${context} failed`, { code, status, reason, message, raw: error });
}

export default function useDialogGoogleClassroomImportUsers({ open, onImportUsers }) {
  const { t } = useTranslate();

  const [loading, setLoading] = useState(false);
  const [loadingClass, setLoadingClass] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      setHasError(false);
      setHasLoaded(false);
      authenticateClassroom(onSuccessAuthClassroom, onErrorAuthClassroom);
    } else {
      setSelectedStudents([]);
    }
  }, [open]);

  /**
   * Callback triggered if the auth in G Classroom API was OK.
   * After OK lets request the classrooms of the authenticated user
   */
  function onSuccessAuthClassroom() {
    getClassrooms(
      (response) => {
        // Setter siempre autoritativo: si la cuenta recién autenticada no tiene cursos,
        // response.result.courses es undefined y debemos vaciar la lista (no conservar la
        // de una cuenta anterior al reabrir el diálogo con otra cuenta).
        const courses = response?.result?.courses ?? [];
        setClassrooms(
          courses.map((course) => {
            return { ...course, students: [], active: false, guid: course.id };
          })
        );
        setHasLoaded(true);
        setLoading(false);
      },
      (error) => {
        logClassroomError('courses.list', error);
        setHasError(true);
        setLoading(false);
      }
    );
  }

  /**
   * Fallback triggered if anything went wrong in the auth with the G Classroom API
   * @param {The error returned for the G Clasroom API} error
   */
  function onErrorAuthClassroom(error) {
    logClassroomError('auth', error);
    setHasError(true);
    setLoading(false);
  }

  /**
   * Listener for show hide the students of a classroom
   * If selected is true, the classroom exists and students length equals 0
   * Then lets request G Classroom API for the students of the selected classroom
   * @param {Guid of the class} theClass
   * @param {flag indicating if is selected or not} selected
   */
  function onChangeSelected(theClass, selected) {
    let theClassroom = classrooms.filter((c) => theClass === c.guid);
    let newClasses = classrooms.map((classroom) => {
      {
        return classroom.guid === theClass ? { ...classroom, active: selected } : classroom;
      }
    });
    setClassrooms(newClasses);

    if (theClassroom && theClassroom.length > 0) {
      theClassroom = theClassroom[0];
      if (selected) {
        setLoadingClass(theClass);
        getClassroomStudents(theClass, onSetClassroomStudents, (error) => {
          logClassroomError('students.list', error);
          setLoadingClass(null);
        });
      } else {
        //Lets remove the students of the class unselected
        let remainingStudents = removeOBjectsFromArrayBasedOnAnotherArray(selectedStudents, theClassroom.students);
        setSelectedStudents(remainingStudents);
      }
    }
  }

  /**
   * Callback return the students of the selected classroom
   * First parse the students, set all as selected
   * Pass the students as an array to the selected classroom
   * @param {The guid of the classroom} classroomGuid
   * @param {The list of the students returned by G Classroom API} students
   */
  function onSetClassroomStudents(classroomGuid, students) {
    if (students.students) {
      let parsedStudents = students.students.map((student) => {
        return {
          selected: true,
          guid: student.userId,
          avatar: student.profile.photoUrl,
          name: student.profile.name.givenName,
          lastname: student.profile.name.familyName,
          email: student.profile.emailAddress,
        };
      });
      let newClassrooms = classrooms.map((theClass) => {
        return theClass.guid === classroomGuid
          ? {
              ...theClass,
              active: true,
              students: parsedStudents,
            }
          : theClass;
      });
      setClassrooms(newClassrooms);
      setSelectedStudents([...selectedStudents, ...parsedStudents]);
    }
    setLoadingClass(null);
  }

  function onChangeSelectedStudents(classroomGuid, studentGuid, selected) {
    let newClassrooms = [];
    for (const theClassroom of classrooms) {
      newClassrooms = [
        ...newClassrooms,
        {
          ...theClassroom,
          students: theClassroom.students.map((student) => {
            {
              return student.guid === studentGuid ? modifySelectedStudents(student, selected) : student;
            }
          }),
        },
      ];
    }

    setClassrooms(newClassrooms);
  }

  function modifySelectedStudents(student, selected) {
    if (selected) {
      setSelectedStudents([...selectedStudents, student]);
      return { ...student, selected: true };
    } else {
      let newStudents = selectedStudents.filter((s) => s.guid !== student.guid);
      setSelectedStudents(newStudents);
      return { ...student, selected: false };
    }
  }

  function onSave() {
    let students = selectedStudents.map((student) => {
      return {
        name: student.name,
        lastname: student.lastname,
        role: 'student',
        username: student.email,
        identity_provider: 'google',
      };
    });
    onImportUsers({ students: students });
  }

  const isEmpty = hasLoaded && !hasError && classrooms.length === 0;

  return {
    t,
    loading,
    loadingClass,
    classrooms,
    selectedStudents,
    hasError,
    isEmpty,
    onChangeSelected,
    onChangeSelectedStudents,
    onSave,
  };
}
